import { v4 as uuid } from 'uuid'

import { invokeAnalyticsAPI } from './client'
import { removeAnalyticsEventListeners } from './domEvents'
import { AnalyticsEvent, CreateSessionPayload } from './types'
import {
  addIdToEvents,
  fetchBrowserDetails,
  getAccessToken,
  getCurrrentSession,
  retrieveStoredUtmParams,
  userActivityEvents
} from './utils'

let eventsQueue: AnalyticsEvent[] = []
let intervalId: NodeJS.Timeout | null = null
const EVENTS_FLUSH_DURATION = 2000
const analyticsCategoryPayload = {
  category: 'portal',
  categoryDetails: {
    appName: ''
  }
}

const cleanupOnSessionTermination = () => {
  localStorage.removeItem('session')
  localStorage.removeItem('appName')
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
  eventsQueue = []
  removeAnalyticsEventListeners()
}

const processAnalyticsEventsQueue = async () => {
  if (eventsQueue.length === 0) return

  const eventsToProcess = [...eventsQueue]

  eventsQueue = []

  try {
    const { id } = getCurrrentSession()

    if (!id) {
      console.error('Cannot add events without a session')

      return
    }

    const appName = localStorage.getItem('appName')
    const accessToken = await getAccessToken()

    const addEventsRequestBody = {
      ...analyticsCategoryPayload,
      categoryDetails: { ...analyticsCategoryPayload.categoryDetails, appName },
      events: eventsToProcess
    }

    await invokeAnalyticsAPI({
      endpoint: `/v1/analytics/session/${id}/events`,
      accessToken,
      method: 'POST',
      body: addEventsRequestBody
    })
  } catch (err) {
    console.error(`Failed to add events: ${err}`)
    eventsQueue = [...eventsToProcess, ...eventsQueue]
  }
}

export const addEvents = (events: AnalyticsEvent[]) => {
  try {
    events = addIdToEvents(events)
    eventsQueue.push(...events)
  } catch (err) {
    console.error(`Failed to queue events: ${err}`)
  }
}

export const createSession = async ({
  appName,
  email
}: CreateSessionPayload): Promise<void> => {
  try {
    const accessToken = await getAccessToken()
    const browserDetails = fetchBrowserDetails()
    const utmParams = retrieveStoredUtmParams()

    const session = {
      id: uuid(),
      startTime: new Date().toISOString(),
      details: {
        ...browserDetails,
        ...utmParams,
        email,
        domainName: window.location.hostname,
        referrer: localStorage.getItem('referrer')
      }
    }

    const createSessionRequestBody = {
      ...analyticsCategoryPayload,
      categoryDetails: {
        ...analyticsCategoryPayload.categoryDetails,
        appName
      },
      session
    }

    await invokeAnalyticsAPI({
      endpoint: '/v1/analytics/session',
      accessToken,
      method: 'POST',
      body: createSessionRequestBody
    })

    localStorage.setItem('session', JSON.stringify(session))
    localStorage.setItem('appName', appName)
  } catch (error) {
    console.error(`Failed to create session: ${(error as Error).message}`)
  }
}

export const endSession = async (): Promise<void> => {
  try {
    const session = getCurrrentSession()

    if (!session?.id) return

    const accessToken = await getAccessToken()
    const appName = localStorage.getItem('appName')

    const addEventsRequestBody = {
      ...analyticsCategoryPayload,
      categoryDetails: { ...analyticsCategoryPayload.categoryDetails, appName },
      events: [
        ...eventsQueue,
        {
          id: uuid(),
          type: userActivityEvents.userLoggedOut,
          details: { email: session?.details?.email },
          timestamp: new Date().toISOString()
        }
      ]
    }

    const updateSessionRequestBody = {
      ...analyticsCategoryPayload,
      categoryDetails: { ...analyticsCategoryPayload.categoryDetails, appName },
      session: { ...session, endTime: new Date().toISOString() }
    }

    await invokeAnalyticsAPI({
      endpoint: `/v1/analytics/session/${session?.id}/events`,
      accessToken,
      method: 'POST',
      body: addEventsRequestBody
    })
    await invokeAnalyticsAPI({
      endpoint: `/v1/analytics/session/${session?.id}`,
      accessToken,
      method: 'PATCH',
      body: updateSessionRequestBody
    })

    cleanupOnSessionTermination()
  } catch (err) {
    console.error(`Failed to end session: ${err}`)
  }
}

export const flushAnalyticsEventsOnUnload = async () => {
  try {
    if (eventsQueue.length > 0) {
      await processAnalyticsEventsQueue()
    }
  } catch (err) {
    console.error(`Failed to flush events on unload: ${err}`)
  }
}

export const startAnalyticsEventFlushInterval = () => {
  if (!intervalId) {
    intervalId = setInterval(processAnalyticsEventsQueue, EVENTS_FLUSH_DURATION)
  }
}
